import { SalonIcon } from "./SalonIcon";
import { useEffect, useMemo, useState } from "react";
import type { PlatformApiClient } from "../api";
import { PlatformApiError } from "../api";
import type { SalonOverview, SalonReports } from "./types";
import { ActionButton, ActionToolbar } from "../adaptive/actions";

type PeriodPreset = "today" | "7d" | "month" | "custom";

function errorText(error: unknown): string {
  if (error instanceof PlatformApiError) return error.message;
  return "No se pudieron cargar los reportes.";
}

function dateKey(value: Date | string, timeZone: string): string {
  const date = typeof value === "string" ? new Date(value) : value;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? "";
  return part("year") + "-" + part("month") + "-" + part("day");
}

function addDays(key: string, days: number): string {
  const values = key.split("-").map(Number);
  const date = new Date(Date.UTC(values[0]!, values[1]! - 1, values[2]! + days));
  return [
    date.getUTCFullYear(),
    String(date.getUTCMonth() + 1).padStart(2, "0"),
    String(date.getUTCDate()).padStart(2, "0"),
  ].join("-");
}

function zoneParts(value: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hourCycle: "h23",
  }).formatToParts(value);
  const read = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");
  return {
    year: read("year"),
    month: read("month"),
    day: read("day"),
    hour: read("hour"),
    minute: read("minute"),
    second: read("second"),
  };
}

function zonedMidnightIso(key: string, timeZone: string): string {
  const values = key.split("-").map(Number);
  const desired = Date.UTC(values[0]!, values[1]! - 1, values[2]!, 0, 0, 0);
  let guess = desired;
  for (let index = 0; index < 3; index += 1) {
    const actual = zoneParts(new Date(guess), timeZone);
    const actualUtc = Date.UTC(
      actual.year,
      actual.month - 1,
      actual.day,
      actual.hour,
      actual.minute,
      actual.second,
    );
    guess += desired - actualUtc;
  }
  return new Date(guess).toISOString();
}

function rangeFor(
  preset: PeriodPreset,
  timeZone: string,
  customFrom: string,
  customTo: string,
) {
  const today = dateKey(new Date(), timeZone);
  if (preset === "custom") {
    const safeFrom = customFrom || today;
    const safeTo = customTo && customTo >= safeFrom ? customTo : safeFrom;
    return {
      from: zonedMidnightIso(safeFrom, timeZone),
      to: zonedMidnightIso(addDays(safeTo, 1), timeZone),
      label: safeFrom + " → " + safeTo,
    };
  }
  const fromKey =
    preset === "7d" ? addDays(today, -6) :
    preset === "month" ? today.slice(0, 7) + "-01" :
    today;
  return {
    from: zonedMidnightIso(fromKey, timeZone),
    to: zonedMidnightIso(addDays(today, 1), timeZone),
    label: preset === "7d" ? "Últimos 7 días" : preset === "month" ? "Este mes" : "Hoy",
  };
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

function statusLabel(value: string): string {
  if (value === "confirmed") return "Confirmadas";
  if (value === "completed") return "Completadas";
  if (value === "cancelled") return "Canceladas";
  if (value === "no_show") return "No-shows";
  if (value === "pending") return "Pendientes";
  if (value === "approved") return "Aprobado";
  if (value === "rejected") return "Rechazado";
  if (value === "partially_refunded") return "Reembolso parcial";
  if (value === "refunded") return "Reembolsado";
  if (value === "created") return "Creado";
  return value;
}

function sourceLabel(value: string): string {
  if (value === "whatsapp") return "WhatsApp";
  if (value === "voice") return "Voz";
  if (value === "manual") return "Manual";
  if (value === "instagram") return "Instagram";
  if (value === "messenger") return "Messenger";
  return value;
}

function paymentMethodLabel(value: string): string {
  if (value === "cash") return "Efectivo";
  if (value === "bank_transfer") return "Transferencia";
  if (value === "external_card") return "Tarjeta externa";
  if (value === "mercadopago") return "Mercado Pago";
  if (value === "other") return "Otro";
  return value;
}

function csvCell(value: string | number): string {
  return '"' + String(value).replaceAll('"', '""') + '"';
}

function reportCsv(snapshot: SalonReports): string {
  const rows: Array<Array<string | number>> = [
    ["seccion", "dimension", "valor", "moneda"],
    ["bookings", "total", snapshot.bookings.total, ""],
    ["bookings", "no_show", snapshot.bookings.noShows, ""],
  ];
  for (const item of snapshot.bookings.byStatus) {
    rows.push(["booking_status", item.status, item.count, ""]);
  }
  for (const item of snapshot.bookings.byLocation) {
    rows.push(["booking_location", item.locationName, item.count, ""]);
  }
  for (const item of snapshot.bookings.byProfessional) {
    rows.push(["booking_professional", item.professionalName, item.count, ""]);
  }
  for (const item of snapshot.financial.byCurrency) {
    rows.push(["sales_collected", item.currency, item.collectedAmountMinor, item.currency]);
    rows.push(["sales_pending", item.currency, item.pendingAmountMinor, item.currency]);
  }
  for (const item of snapshot.financial.collectionsByMethod) {
    rows.push(["collection_method", item.method, item.netAmountMinor, item.currency]);
  }
  for (const item of snapshot.financial.mercadoPago) {
    rows.push(["mercadopago_" + item.status, "collections", item.netAmountMinor, item.currency]);
  }
  return rows.map((row) => row.map(csvCell).join(",")).join("\n") + "\n";
}

export function SalonsReports(props: {
  organizationId: string;
  overview: SalonOverview;
  api: PlatformApiClient;
}) {
  const today = dateKey(new Date(), props.overview.timeZone);
  const [period, setPeriod] = useState<PeriodPreset>("month");
  const [customFrom, setCustomFrom] = useState(today);
  const [customTo, setCustomTo] = useState(today);
  const [locationId, setLocationId] = useState("");
  const [professionalId, setProfessionalId] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [source, setSource] = useState("");
  const [snapshot, setSnapshot] = useState<SalonReports | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  const range = useMemo(
    () => rangeFor(period, props.overview.timeZone, customFrom, customTo),
    [period, props.overview.timeZone, customFrom, customTo],
  );

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError(null);
    props.api.salonReports(
      props.organizationId,
      range.from,
      range.to,
      20,
      {
        locationId: locationId || null,
        professionalId: professionalId || null,
        serviceId: serviceId || null,
        source: source || null,
      },
    )
      .then((next) => { if (current) setSnapshot(next); })
      .catch((cause) => {
        if (!current) return;
        setSnapshot(null);
        setError(errorText(cause));
      })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [
    props.api,
    props.organizationId,
    range.from,
    range.to,
    locationId,
    professionalId,
    serviceId,
    source,
    reloadNonce,
  ]);

  const primary = snapshot?.financial.byCurrency.find((item) => item.currency === "ARS")
    ?? snapshot?.financial.byCurrency[0]
    ?? null;
  const currency = primary?.currency ?? "ARS";
  const hasFilters = Boolean(locationId || professionalId || serviceId || source);

  function exportCsv() {
    if (!snapshot) return;
    const url = URL.createObjectURL(
      new Blob([reportCsv(snapshot)], { type: "text/csv;charset=utf-8" }),
    );
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "clickdialog-salons-report-" + dateKey(new Date(), props.overview.timeZone) + ".csv";
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  }

  function clearFilters() {
    setLocationId("");
    setProfessionalId("");
    setServiceId("");
    setSource("");
  }

  return <section className="cd-roi-reports" aria-labelledby="roi-reports-title">
    <div className="cd-page-frame">
      <header className="cd-page-header">
        <div>
          <p className="cd-page-header__eyebrow">ROI ClickDialog</p>
          <h1 id="roi-reports-title" className="cd-page-header__title">Reportes</h1>
          <p className="cd-page-header__description">
            Analizá reservas, ventas y clientes desde datos canónicos verificables.
          </p>
        </div>
      </header>

      <div className="cd-roi-reports__period cd-control-panel" role="group" aria-label="Período">
        <span className="cd-roi-reports__control-label cd-control-label">Período</span>
        <div className="cd-roi-reports__period-chips cd-control-row">
          {([["today", "Hoy"], ["7d", "7 días"], ["month", "Este mes"], ["custom", "Personalizado"]] as const).map(([key,label]) => <button
            key={key}
            type="button"
            className={"cd-roi-reports__chip cd-button cd-button--sm " + (period === key ? "cd-roi-reports__chip--active cd-button--secondary" : "cd-button--ghost")}
            aria-pressed={period === key}
            onClick={() => setPeriod(key)}
          >{label}</button>)}
        </div>
      </div>

      {period === "custom" && <div className="cd-roi-reports__custom-range cd-control-panel" aria-label="Período personalizado">
        <label>
          <span>Desde</span>
          <input type="date" value={customFrom} max={customTo} onChange={(event) => setCustomFrom(event.target.value)} />
        </label>
        <label>
          <span>Hasta</span>
          <input type="date" value={customTo} min={customFrom} onChange={(event) => setCustomTo(event.target.value)} />
        </label>
      </div>}

      <section className="cd-roi-reports__filters cd-control-panel" aria-label="Filtros de reportes">
        <label>
          <span>Local</span>
          <select value={locationId} onChange={(event) => setLocationId(event.target.value)}>
            <option value="">Todos</option>
            {snapshot?.filterOptions.locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          <span>Profesional</span>
          <select value={professionalId} onChange={(event) => setProfessionalId(event.target.value)}>
            <option value="">Todos</option>
            {snapshot?.filterOptions.professionals.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          <span>Servicio</span>
          <select value={serviceId} onChange={(event) => setServiceId(event.target.value)}>
            <option value="">Todos</option>
            {snapshot?.filterOptions.services.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
        <label>
          <span>Origen de venta</span>
          <select value={source} onChange={(event) => setSource(event.target.value)}>
            <option value="">Todos</option>
            {snapshot?.filterOptions.sources.map((item) => <option key={item} value={item}>{sourceLabel(item)}</option>)}
          </select>
        </label>
        <ActionToolbar className="cd-roi-reports__filter-actions" label="Acciones de reportes">
          <ActionButton variant="ghost" onClick={clearFilters} disabled={!hasFilters}>Limpiar filtros</ActionButton>
          <ActionButton variant="tertiary" onClick={exportCsv} disabled={!snapshot}>Exportar CSV</ActionButton>
        </ActionToolbar>
        {source && <p className="cd-roi-reports__metric-note">El filtro de origen usa el dato explícito guardado en la venta y sólo afecta métricas de venta; no se convierte en “origen de reserva”.</p>}
      </section>

      {loading && <div className="cd-roi-reports__state cd-state-card"><p className="cd-roi-reports__state-title">Cargando reportes…</p></div>}
      {error && <div className="cd-roi-reports__state cd-roi-reports__state--error cd-state-card cd-state-card--error" role="alert">
        <p className="cd-roi-reports__state-title">No pudimos cargar Reportes.</p>
        <p className="cd-roi-reports__state-text">{error}</p>
        <ActionButton variant="secondary" onClick={() => setReloadNonce((value) => value + 1)}>Reintentar</ActionButton>
      </div>}

      {!loading && snapshot && <div className="cd-roi-reports__content">
        <div className="cd-roi-reports__insight cd-action-status" role="status">
          <p className="cd-roi-reports__insight-text">
            {snapshot.bookings.total} reservas · {snapshot.financial.activeSaleCount} ventas activas · {range.label}
          </p>
        </div>

        <p className="cd-roi-reports__data-note sr-only">
          Finanzas se muestran por moneda. ClickDialog no mezcla monedas ni atribuye ingresos a IA sin provenance explícito.
        </p>

        <section className="cd-roi-reports__mobile-summary" aria-label="Resumen ejecutivo">
          <article className="cd-roi-reports__mobile-hero">
            <p className="cd-roi-reports__mobile-hero-label">Cobrado</p>
            <p className="cd-roi-reports__mobile-hero-amount">{money(primary?.collectedAmountMinor ?? 0, currency)}</p>
            <p className="cd-roi-reports__mobile-hero-copy">Ventas canónicas del período</p>
            <p className="cd-roi-reports__mobile-hero-meta">{snapshot.bookings.total} reservas · {snapshot.financial.activeSaleCount} ventas</p>
          </article>
          <div className="cd-roi-reports__mobile-secondary">
            <article className="cd-roi-reports__mobile-stat"><span>Por cobrar</span><strong>{money(primary?.pendingAmountMinor ?? 0, currency)}</strong></article>
            <article className="cd-roi-reports__mobile-stat"><span>Reservas</span><strong>{snapshot.bookings.total}</strong></article>
            <article className="cd-roi-reports__mobile-stat"><span>Ticket</span><strong>{money(primary?.averageTicketMinor ?? 0, currency)}</strong></article>
            <article className="cd-roi-reports__mobile-stat"><span>No-shows</span><strong>{snapshot.bookings.noShows}</strong></article>
          </div>
        </section>

        <section className="cd-roi-reports__kpis cd-grid cd-grid--3" aria-label="Indicadores principales">
          {snapshot.financial.byCurrency.map((item) => <article key={"collected-"+item.currency} className="cd-roi-reports__kpi cd-roi-reports__kpi--primary">
            <SalonIcon name="payments" className="cd-salons-visual-icon cd-salons-visual-icon--green" /><span>Ingresos cobrados · {item.currency}</span><strong>{money(item.collectedAmountMinor, item.currency)}</strong>
          </article>)}
          {snapshot.financial.byCurrency.map((item) => <article key={"pending-"+item.currency} className="cd-roi-reports__kpi">
            <SalonIcon name="schedule" className="cd-salons-visual-icon cd-salons-visual-icon--warm" /><span>Pendiente · {item.currency}</span><strong>{money(item.pendingAmountMinor, item.currency)}</strong>
          </article>)}
          <article className="cd-roi-reports__kpi"><SalonIcon name="calendar_today" className="cd-salons-visual-icon cd-salons-visual-icon--warm" /><span>Reservas totales</span><strong>{snapshot.bookings.total}</strong></article>
          <article className="cd-roi-reports__kpi"><SalonIcon name="event_available" className="cd-salons-visual-icon" /><span>Reservas confirmadas</span><strong>{snapshot.bookings.byStatus.find((item) => item.status === "confirmed")?.count ?? 0}</strong></article>
          <article className="cd-roi-reports__kpi"><SalonIcon name="schedule" className="cd-salons-visual-icon" /><span>Reservas pendientes</span><strong>{snapshot.bookings.byStatus.find((item) => item.status === "pending")?.count ?? 0}</strong></article>
          {snapshot.financial.byCurrency.map((item) => <article key={"ticket-"+item.currency} className="cd-roi-reports__kpi">
            <SalonIcon name="receipt_long" className="cd-salons-visual-icon cd-salons-visual-icon--violet" /><span>Ticket promedio · {item.currency}</span><strong>{money(item.averageTicketMinor, item.currency)}</strong>
          </article>)}
        </section>

        <div className="cd-roi-reports__grid cd-grid cd-grid--2">
          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-status-title">
            <h2 id="roi-status-title">Reservas por estado</h2>
            <ul className="cd-roi-reports__bucket-list">
              {snapshot.bookings.byStatus.map((item) => <li key={item.status}><span>{statusLabel(item.status)}</span><strong>{item.count}</strong><progress className="cd-salons-report-bar" aria-label={statusLabel(item.status)} value={item.count} max={Math.max(1, snapshot.bookings.total)} /></li>)}
            </ul>
          </section>

          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-customers-title">
            <h2 id="roi-customers-title">Clientes nuevos / retornantes</h2>
            <div className="cd-salons-donut" role="img" aria-label={snapshot.customers.new + " clientes nuevos y " + snapshot.customers.returning + " retornantes"} style={{ background: snapshot.customers.new + snapshot.customers.returning === 0 ? "var(--cd-color-border)" : "conic-gradient(var(--cd-color-brand-600) " + (snapshot.customers.new / (snapshot.customers.new + snapshot.customers.returning) * 100) + "%, var(--cd-color-brand-100) 0)" }}>
              <div><strong>{snapshot.customers.new + snapshot.customers.returning}</strong><span>Clientes</span></div>
            </div>
            <ul className="cd-roi-reports__bucket-list">
              <li><span>Clientes nuevos</span><strong>{snapshot.customers.new}</strong><progress className="cd-salons-report-bar" aria-label="Clientes nuevos" value={snapshot.customers.new} max={Math.max(1, snapshot.customers.new + snapshot.customers.returning)} /></li>
              <li><span>Clientes retornantes</span><strong>{snapshot.customers.returning}</strong><progress className="cd-salons-report-bar" aria-label="Clientes retornantes" value={snapshot.customers.returning} max={Math.max(1, snapshot.customers.new + snapshot.customers.returning)} /></li>
            </ul>
            <p className="cd-roi-reports__metric-note">Nuevo = primer turno registrado dentro del período. Retornante = actividad en el período y al menos un turno anterior.</p>
          </section>

          <section className="cd-roi-reports__card cd-salons-ranking-card cd-page-section" aria-labelledby="roi-services-title">
            <h2 id="roi-services-title">Top servicios</h2>
            <ol className="cd-salons-service-ranking">
              {[...snapshot.topServices].sort((a, b) => b.appointmentCount - a.appointmentCount).map((item, index) => <li key={item.serviceId}>
                <span className="cd-salons-service-rank" aria-hidden="true">{index + 1}</span>
                <div className="cd-salons-service-result">
                  <div className="cd-salons-service-label"><strong>{item.serviceName}</strong><span>{item.appointmentCount} reservas</span></div>
                  <progress className="cd-salons-report-bar" aria-label={"Reservas de " + item.serviceName} value={item.appointmentCount} max={Math.max(1, ...snapshot.topServices.map((service) => service.appointmentCount))} />
                  <p>{item.salesByCurrency.map((sale) => sale.saleCount + " ventas · " + money(sale.collectedAmountMinor, sale.currency) + " cobrado · " + money(sale.pendingAmountMinor, sale.currency) + " pendiente").join(" / ") || "Sin ventas registradas"}</p>
                </div>
              </li>)}
            </ol>
          </section>

          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-professionals-title">
            <h2 id="roi-professionals-title">Top profesionales</h2>
            <ul className="cd-roi-reports__bucket-list">
              {snapshot.bookings.byProfessional.length === 0 && <li><span>Sin asignaciones profesionales</span><strong>0</strong></li>}
              {snapshot.bookings.byProfessional.map((item) => <li key={item.professionalId}>
                <span>{item.professionalName}</span>
                <strong>{item.count}</strong>
                <progress className="cd-salons-report-bar" aria-label={"Reservas de " + item.professionalName} value={item.count} max={Math.max(1, snapshot.bookings.total)} />
              </li>)}
            </ul>
          </section>

          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-locations-title">
            <h2 id="roi-locations-title">Reservas por local</h2>
            <ul className="cd-roi-reports__bucket-list">
              {snapshot.bookings.byLocation.map((item) => <li key={item.locationId ?? "none"}>
                <span>{item.locationName}</span>
                <strong>{item.count}</strong>
                <progress className="cd-salons-report-bar" aria-label={"Reservas en " + item.locationName} value={item.count} max={Math.max(1, snapshot.bookings.total)} />
              </li>)}
            </ul>
          </section>

          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-payments-title">
            <h2 id="roi-payments-title">Cobros por método</h2>
            <ul className="cd-roi-reports__bucket-list">
              {snapshot.financial.collectionsByMethod.length === 0 && <li><span>Sin cobros registrados</span><strong>0</strong></li>}
              {snapshot.financial.collectionsByMethod.map((item) => <li key={item.method+"-"+item.currency}>
                <span>{paymentMethodLabel(item.method)} · {item.currency}</span>
                <strong>{money(item.netAmountMinor, item.currency)}</strong>
              </li>)}
            </ul>
          </section>

          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-mp-title">
            <h2 id="roi-mp-title">Mercado Pago</h2>
            <ul className="cd-roi-reports__bucket-list">
              {snapshot.financial.mercadoPago.length === 0 && <li><span>Sin hechos de proveedor en el período</span><strong>0</strong></li>}
              {snapshot.financial.mercadoPago.map((item) => <li key={item.status+"-"+item.currency}>
                <span>{statusLabel(item.status)} · {item.currency}</span>
                <strong>{item.collectionCount} · {money(item.netAmountMinor, item.currency)}</strong>
              </li>)}
            </ul>
            <p className="cd-roi-reports__metric-note">Se muestran hechos del proveedor; no se presentan como settlement bancario.</p>
          </section>

          <section className="cd-roi-reports__card cd-page-section" aria-labelledby="roi-source-title">
            <h2 id="roi-source-title">Ventas por origen</h2>
            <ul className="cd-roi-reports__bucket-list">
              {snapshot.salesBySource.length === 0 && <li><span>Sin origen registrado</span><strong>0</strong></li>}
              {snapshot.salesBySource.map((item) => <li key={item.source+"-"+item.currency}>
                <span>{sourceLabel(item.source)} · {item.currency}</span>
                <strong>{item.saleCount} · {money(item.collectedAmountMinor, item.currency)}</strong>
              </li>)}
            </ul>
            <p className="cd-roi-reports__metric-note">El origen es un dato explícito guardado en la venta; no significa “generado por IA”.</p>
          </section>

          <details className="cd-roi-reports__card cd-page-section cd-salons-report-limits"><summary>Métricas no disponibles todavía</summary>
            <h2 id="roi-limits-title">Métricas pendientes de provenance</h2>
            <ul className="cd-roi-reports__usage-list">
              <li><span>Ingresos generados por IA</span><strong>No disponible</strong></li>
              <li><span>Reservas generadas por IA</span><strong>No disponible</strong></li>
              <li><span>Uso IA / automatizaciones</span><strong>No disponible</strong></li>
            </ul>
            <p className="cd-roi-reports__metric-note">No se inferirá IA desde WhatsApp, modo autónomo ni coincidencia temporal.</p>
          </details>
        </div>
      </div>}
    </div>
  </section>;
}
